import 'mapbox-gl/dist/mapbox-gl.css';

import React from 'react';
import MapboxGL from 'mapbox-gl';
import TurfDifference from '@turf/difference';
import TurfBBoxPolygon from '@turf/bbox-polygon';
import TurfTransformTranslate from '@turf/transform-translate';
import { polygon as TurfPolygon, point as TurfPoint } from '@turf/helpers';
import TurfDestination from '@turf/destination';
import ReactDOM from 'react-dom';
import MapInfo from './info';
import MapInfoGroup from './info-group';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import TurfDistance from '@turf/distance';

MapboxGL.accessToken = 'pk.eyJ1Ijoid3lyenl0IiwiYSI6ImNqaDVrbzNiYzFhcWQycW8yb2g1Z2dscTEifQ.Zp-ZYiSZN5jHjykg2Tq4Sw';

class Map extends React.Component {

  static layerInfo = [
    ['red', '#f00'],
    ['yellow', '#ff0'],
    ['blue', '#00f'],
    ['green', '#0f0']
  ];

  onReference = reference => {
    this.mapContainer = reference;
  };

  componentDidMount() {
    this.initialiseMap()
      .catch(console.error);
  }

  componentDidUpdate() {
    // It should only be updating based on props
    this.updateMap();
  }

  static getRegionBounds(region, paddingInKilometers = 100) {
    const regionBounds = region.geometry.coordinates
      .reduce(
        (bounds, polygon) => {
          return polygon.reduce(
            (bounds, [latitude, longitude]) => {
              // Ignore elevation
              return bounds.extend([latitude, longitude]);
            },
            bounds
          )
        },
        new MapboxGL.LngLatBounds()
      );

    regionBounds.setNorthEast(
      TurfDestination(
        regionBounds.getNorthEast().toArray(),
        paddingInKilometers,
        45
      )
        .geometry.coordinates
    );

    regionBounds.setSouthWest(
      TurfDestination(
        regionBounds.getSouthWest().toArray(),
        paddingInKilometers,
        225
      )
        .geometry.coordinates
    );

    return regionBounds;
  }

  async initialiseMap() {
    this.clearPopup();

    if (this.map) {
      this.map.remove();
    }

    const regionBounds = Map.getRegionBounds(this.props.region);

    const images = await Map.loadImagesForLayers();

    this.map = new MapboxGL.Map({
      container: this.mapContainer,
      style: 'mapbox://styles/wyrzyt/cjhifgywy1oyy2smwc8dy82pb',
      attributionControl: false,
      zoom: 4,
      maxBounds: regionBounds,
      center: regionBounds.getCenter()
    });

    this.map.addControl(new MapboxGL.NavigationControl());

    this.currentRegion = this.props.region;
    this.currentData = Map.filterLocationData(this.props.data);

    this.map.on('load', () => {
      Map.layerInfo
        .forEach(([id], index) => {
          this.map.addImage(`badge${id}`, images[index]);
        });
      this.onMapLoad();
    });


  }

  updateMap() {
    if (!(this.map && this.map.loaded())) {
      return;
    }
    if (this.currentRegion !== this.props.region) {
      this.updateRegionData(this.props.region);
    }
    if (this.currentData !== this.props.data) {
      this.updateFeatureData(this.props.data);
    }
  }

  static getRegionMask(region, maximumBounds = this.map.getMaxBounds() || this.map.getBounds()) {
    const clonedBounds = new MapboxGL.LngLatBounds(...maximumBounds.toArray());

    const expandInKilometers = 200;

    // Extend past the maximumBounds, as I have seen sometimes the bounds be broken out off...
    clonedBounds.setNorthEast(
      TurfDestination(
        clonedBounds.getNorthEast().toArray(),
        expandInKilometers,
        45
      )
        .geometry.coordinates
    );

    clonedBounds.setSouthWest(
      TurfDestination(
        clonedBounds.getSouthWest().toArray(),
        expandInKilometers,
        225
      )
        .geometry.coordinates
    );

    return TurfDifference(
      TurfBBoxPolygon([
        clonedBounds.getWest(),
        clonedBounds.getSouth(),
        clonedBounds.getEast(),
        clonedBounds.getNorth()
      ]),
      TurfPolygon(region.geometry.coordinates)
    );
  }

  updateRegionData(region) {
    this.currentRegion = region;
    const maximumBounds = Map.getRegionBounds(region);
    const mask = Map.getRegionMask(region, maximumBounds);
    this.map.getSource('region').setData(mask);
  }

  /**
   * Adds a semi-transparent mask outside of the region
   */
  addRegionMask() {
    const maximumBounds = this.map.getMaxBounds() || this.map.getBounds();

    this.map.addSource('region', {
      type: 'geojson',
      data: Map.getRegionMask(this.props.region, maximumBounds)
    });

    this.map.addLayer({
      id: 'region',
      source: 'region',
      type: 'fill',
      paint: {
        'fill-color': 'white',
        'fill-opacity': 0.75
      }
    });
  }

  static filterLocationData(data) {
    return {
      ...data,
      features: data
        .features
        .filter(feature => feature.properties && /(Locations|Tier 1)/.test(feature.properties.customer))
    }
  }

  updateFeatureData(data) {
    this.currentData = Map.filterLocationData(data);
    this.map.getSource('locations').setData(this.currentData);
  }

  static async loadImagesForLayers() {
    const promises = Map.layerInfo
      .map(
        async ([ id ], index) => {
          const size = 40 - (index * 5);
          const image = new Image(size, size);
          const promise = new Promise(
            (resolve, reject) => {
              image.onerror = reject;
              image.onload = resolve;
            }
          );
          image.src = `/assets/images/badges/badge-${id}.svg`;
          await promise;
          return image;
        }
      );
    return Promise.all(promises);
  }

  /**
   * Add our location features to the map
   */
  addFeatureInfo() {
    this.map.addSource('locations', {
      type: 'geojson',
      data: Map.filterLocationData(this.props.data)
    });

    // Layer the most important features on top of the least important
    Map.layerInfo
      .reduce(
        (before, [ id ]) => {
          this.map.addLayer({
            id: id,
            type: 'symbol',
            source: 'locations',
            filter: [
              '==', ['get', 'status'], id
            ],
            layout: {
              'icon-image': `badge${id}`,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'text-allow-overlap': true
            }
          }, before);
          return id;
        },
        null
      );
  }

  onMapLoad = () => {
    this.addRegionMask();
    this.addFeatureInfo();
    this.map.on('click', this.onMapClick);
  };

  static isFeaturesClose(features) {
    const bounds = features
      .reduce(
        (bounds, feature) => {
          return bounds.extend(feature.geometry.coordinates);
        },
        new MapboxGL.LngLatBounds()
      );

    const northEast = bounds.getNorthEast().toArray(),
      southWest = bounds.getSouthWest().toArray(),
      distance = TurfDistance(northEast, southWest, { units: 'meters' });

    return distance < 100;
  }

  onSelectMultipleFeatures(features) {
    if (Map.isFeaturesClose(features)) {
      return this.showPopup(
        <MapInfoGroup features={features} />,
        features[0].geometry.coordinates
      );
    }
    const bounds = features.reduce(
      (bounds, feature) => {
        return bounds.extend(feature.geometry.coordinates)
      },
      new MapboxGL.LngLatBounds()
    );

    const northEast = bounds.getNorthEast().toArray(),
      southWest = bounds.getSouthWest().toArray(),
      distance = TurfDistance(northEast, southWest, { units: 'meters' }),
      northEastPoint = TurfPoint(northEast),
      southWestPoint = TurfPoint(southWest),
      // Set the bounds 2/5th out of the total distance across the bounds, will give us a nice padding
      newNorthEastPoint = TurfTransformTranslate(northEastPoint, distance * 0.4, 45, {
        units: 'meters'
      }),
      newSouthWestPoint = TurfTransformTranslate(southWestPoint, distance * 0.4, 225, {
        units: 'meters'
      });

    bounds.setNorthEast(
      newNorthEastPoint.geometry.coordinates
    );
    bounds.setSouthWest(
      newSouthWestPoint.geometry.coordinates
    );

    this.map.fitBounds(
      bounds
    );
  }

  onSelectFeature(feature) {
    this.showPopup(
      <MapInfo feature={feature} />,
      feature.geometry.coordinates
    );
  }

  showPopup(component, coordinates) {
    const element = window.document.createElement('div');
    ReactDOM.render(component, element);
    this.currentPopup = new MapboxGL.Popup({})
      .setLngLat(coordinates)
      .setDOMContent(element)
      .addTo(this.map);
  }

  clearPopup() {
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
  }

  onMapClick = (event) => {
    // Any time the map is clicked, exit out of any popup that is showing
    this.clearPopup();
    const features = this.map.queryRenderedFeatures(
      event.point,
      {
        layers: Map.layerInfo.map(([ id ]) => id)
      }
    );
    if (features.length > 1) {
      this.onSelectMultipleFeatures(features);
    }
    if (features.length === 1) {
      this.onSelectFeature(features[0])
    }
  };

  render() {
    const containerStyle = {
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      backgroundColor: 'grey'
    };

    const mapStyle  = {
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0
    };

    const actionsStyle = {
      position: 'absolute',
      bottom: 0,
      right: 40,
      padding: 15,
    };

    const actionStyle = {
      color: 'black'
    };

    const titleStyle = {
      position: 'absolute',
      bottom: 50,
      left: 10,
      padding: 0
    };

    return (
      <div style={containerStyle}>
        <div style={mapStyle} ref={this.onReference} />
        <div style={actionsStyle}>
          <Link to="/" style={actionStyle}>Exit</Link>
        </div>
      </div>
    )
  }
}

Map.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.oneOf(['FeatureCollection']).isRequired,
    features: PropTypes.arrayOf(
      MapInfo.propTypes.feature
    ).isRequired
  }).isRequired,
  region: PropTypes.shape({
    type: PropTypes.oneOf(['Feature']).isRequired,
    geometry: PropTypes.shape({
      coordinates: PropTypes.arrayOf(
        (value) => {
          return value.length > 0 && value.every(
            item => Array.isArray(item) && item.length > 0 && item.every(
              innerItem => Array.isArray(innerItem) && innerItem.length >= 2 && innerItem.every(
                coordinatePart => typeof coordinatePart === 'number'
              )
            )
          )
        }
      ).isRequired
    }).isRequired
  }).isRequired
};

export default Map;



// WEBPACK FOOTER //
// ./src/app/map/map.js